import React from 'react'
import styled from '@emotion/styled/macro'
import { Dialog, DialogContent, InputLabel, Input, CircularProgress } from '@material-ui/core'
import { ActionButton, StyledDialogActions, StyledDialogTitle } from 'components/widgets'
import { GRAY3_COLOR, TEXTFIELD_LABEL_COLOR } from 'lib/constants'

export interface AddChannelModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (name: string) => Promise<void> | void
  busy?: boolean
  defaultValue?: string
  title?: string
}

const ElementWrapper = styled.div({
  width: '100%',
  alignContent: 'center',
})

const StyledContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '25px',
})

const StyledFormControl = styled.div({
  width: '100%',
  marginBottom: '15px',
})

const StyledInputLabel = styled(InputLabel)({
  color: TEXTFIELD_LABEL_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.5rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginBottom: '20px',
})

const StyledInput = styled(Input)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  padding: '5px 10px',
  fontFamily: 'Montserrat',
})

const AddChannelModal: React.FC<AddChannelModalProps> = ({
  open,
  onClose,
  onSubmit,
  busy = false,
  defaultValue = '',
  title = 'Add Channel',
}) => {
  const [name, setName] = React.useState(defaultValue)
  const [touched, setTouched] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setName(defaultValue || '')
      setTouched(false)
    }
  }, [open, defaultValue])

  const trimmed = name.trim()
  const hasError = touched && trimmed.length === 0

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setTouched(true)
    if (trimmed.length === 0 || busy) return
    await onSubmit(trimmed)
  }

  return (
    <Dialog
      style={{ animationFillMode: 'forwards' }}
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-channel-title"
    >
      <StyledDialogTitle id="add-channel-title">{title}</StyledDialogTitle>
      <DialogContent>
        <ElementWrapper>
          <StyledContainer>
            <div>
              <form onSubmit={handleSubmit}>
                <StyledFormControl>
                  <StyledInputLabel htmlFor="channel-name">Channel Name</StyledInputLabel>
                  <StyledInput
                    id="channel-name"
                    disableUnderline
                    fullWidth
                    placeholder="e.g., Security, Dispatch, Floor 2"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onBlur={() => setTouched(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleSubmit()
                      }
                    }}
                  />
                  {/* If you want inline helper text, add it here (kept minimal to match your reference). */}
                </StyledFormControl>
              </form>
            </div>
          </StyledContainer>

          <StyledDialogActions>
            <ActionButton width="110px" height="28px" onClick={onClose} disabled={busy}>
              Cancel
            </ActionButton>
            <ActionButton
              width="110px"
              height="28px"
              onClick={() => handleSubmit()}
              disabled={busy || trimmed.length === 0}
            >
              {busy ? <CircularProgress size={16} /> : 'Create'}
            </ActionButton>
          </StyledDialogActions>
        </ElementWrapper>
      </DialogContent>
    </Dialog>
  )
}

export default AddChannelModal
